import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface HrPerson {
  mainId: number
  vorname: string | null
  nachname: string | null
  geburtsdatum: string | null
  geburtsort: string | null
  staatsangehoerigkeit: string | null
  geschlecht: string | null
  email: string | null
  handy: string | null
  telefonnummer: string | null
  strasse: string | null
  plz: string | null
  stadt: string | null
  bundesland: string | null
  portraitBlobPath: string | null
  portraitUploadedAt: string | null
}

export interface HrEmployment {
  personalId: number | null
  firma: string | null
  status: string | null
  berufsbezeichnung: string | null
  personalkostenstelle: string | null
  eintrittsdatum: string | null
  austrittsdatum: string | null
  validFrom: string | null
  validTo: string | null
}

export type HrPersonTabId =
  | 'uebersicht'
  | 'stammdaten'
  | 'bank-steuer'
  | 'beschaeftigung'
  | 'vertraege'
  | 'zeiterfassung'
  | 'lohn'
  | 'urlaub'
  | 'krank'
  | 'compliance'
  | 'dokumente'
  | 'notizen'
  | 'historie'
  | 'offboarding'

export const ALL_HR_PERSON_TABS: ReadonlyArray<HrPersonTabId> = [
  'uebersicht', 'stammdaten', 'bank-steuer', 'beschaeftigung', 'vertraege',
  'zeiterfassung', 'lohn', 'urlaub', 'krank', 'compliance', 'dokumente',
  'notizen', 'historie', 'offboarding',
]

export type HrPersonTabAccess = Partial<Record<`hr-person-${HrPersonTabId}`, { read: boolean; write: boolean }>>

export interface HrPersonResponse {
  person: HrPerson
  employment: HrEmployment
  tabAccess: HrPersonTabAccess
  readSource: 'dim' | 'fm'
}

export function tabIdToModuleId(tabId: HrPersonTabId): `hr-person-${HrPersonTabId}` {
  return `hr-person-${tabId}` as const
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function usePersonById(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person', mainId],
    queryFn: () => api.get<HrPersonResponse>(`/api/personen/${mainId}`),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 30_000,
  })
}

// Editable Stammdaten fields — mirror of backend STAMMDATEN_EDITABLE allowlist.
// Keep this union in sync with `hrPerson.js` so any drift surfaces at compile time.
export type StammdatenPatch = Partial<Pick<HrPerson,
  | 'vorname' | 'nachname' | 'geschlecht' | 'geburtsdatum' | 'geburtsort'
  | 'staatsangehoerigkeit' | 'email' | 'handy' | 'telefonnummer'
  | 'strasse' | 'plz' | 'stadt' | 'bundesland'
>>

export function usePatchPerson(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StammdatenPatch) =>
      api.patch<{ ok: true; person: HrPerson }>(`/api/personen/${mainId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-person', mainId] })
      qc.invalidateQueries({ queryKey: ['hr-person-audit', mainId] })
    },
  })
}

// ── Fahrer-Markierung (ops.StaffPortalUser.is_fahrer, Mig 387) ────────────────
// HR-MainID == ops.StaffPortalUser.main_id. hasPortalAccess=false: die Person hat
// keine Portalzeile — die Markierung ist dann nicht setzbar (PUT antwortet 404).

export interface PersonFahrer {
  mainId: number
  hasPortalAccess: boolean
  isFahrer: boolean
}

export function usePersonFahrer(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person-fahrer', mainId],
    queryFn: () => api.get<PersonFahrer>(`/api/personen/${mainId}/fahrer`),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 30_000,
  })
}

export function useSetPersonFahrer(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (fahrer: boolean) =>
      api.put<PersonFahrer>(`/api/personen/${mainId}/fahrer`, { fahrer }),
    onSuccess: (data) => qc.setQueryData(['hr-person-fahrer', mainId], data),
  })
}

// ── Portrait (Azure Blob via Pattern B SAS) ───────────────────────────────────

export type PortraitContentType = 'image/jpeg' | 'image/png' | 'image/webp'

export interface PortraitUrlResponse {
  url: string | null
  expiresAt: string | null
  blobPath?: string
}

export function usePortraitUrl(mainId: number | null | undefined, hasPortrait: boolean) {
  return useQuery({
    queryKey: ['hr-person-portrait-url', mainId],
    queryFn: () => api.get<PortraitUrlResponse>(`/api/personen/${mainId}/portrait-url`),
    enabled: typeof mainId === 'number' && mainId > 0 && hasPortrait,
    // SAS lives 60 min — refresh comfortably before expiry.
    staleTime: 45 * 60 * 1000,
    gcTime:    60 * 60 * 1000,
  })
}

export interface PortraitSasResponse {
  uploadUrl: string
  blobPath: string
  expiresAt: string
}

// Full upload pipeline as one mutation. Steps:
//   1. POST /portrait-sas → SAS URL
//   2. PUT blob to SAS URL (direct to Azure — no FA hop, no 90s SWA ceiling)
//   3. POST /portrait     → commit blobPath into dim.Person
// React Query's mutation surfaces the final `{ blobPath, url, uploadedAt }`.
export function useUploadPortrait(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ blob, contentType }: { blob: Blob; contentType: PortraitContentType }) => {
      const sas = await api.post<PortraitSasResponse>(
        `/api/personen/${mainId}/portrait-sas`, { contentType },
      )
      const put = await fetch(sas.uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type':   contentType,
        },
        body: blob,
      })
      if (!put.ok) {
        throw new Error(`Upload zum Blob-Storage fehlgeschlagen: ${put.status} ${put.statusText}`)
      }
      return api.post<{ ok: true; blobPath: string; url: string; uploadedAt: string }>(
        `/api/personen/${mainId}/portrait`, { blobPath: sas.blobPath },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-person', mainId] })
      qc.invalidateQueries({ queryKey: ['hr-person-portrait-url', mainId] })
      qc.invalidateQueries({ queryKey: ['hr-person-audit', mainId] })
    },
  })
}

export function useDeletePortrait(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.delete<{ ok: true; removed: boolean }>(`/api/personen/${mainId}/portrait`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr-person', mainId] })
      qc.invalidateQueries({ queryKey: ['hr-person-portrait-url', mainId] })
      qc.invalidateQueries({ queryKey: ['hr-person-audit', mainId] })
    },
  })
}

// ── Employment history (dim.PersonEmployment SCD2) ────────────────────────────

export interface EmploymentHistoryRow {
  employmentId: number
  firma: string | null
  personalId: number | null
  status: string | null
  berufsbezeichnung: string | null
  personalkostenstelle: string | null
  validFrom: string
  validTo: string | null
  monatsgehalt: number | null
  monatsgehaltsart: string | null
  stdLohn: number | null
  stdBasiert: string | null
  urlaubsanspruch: number | null
  probezeitMonate: number | null
  ferienteilnahme: string | null
  wochenstunden: number | null
  arbeitstage: string | null
  zeiterfassungAktiv: string | null
}

export function useEmploymentHistory(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person-employment-history', mainId],
    queryFn: () => api.get<{ history: EmploymentHistoryRow[]; count: number }>(
      `/api/personen/${mainId}/employment-history`,
    ),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 60_000,
  })
}

// ── Sick leave (dim.PersonSickLeave) ──────────────────────────────────────────

export interface SickLeaveResponse {
  year: number
  totalDays: number
  byMonth: number[]
  days: string[]
}

export function useSickLeave(mainId: number | null | undefined, year: number) {
  return useQuery({
    queryKey: ['hr-person-sick-leave', mainId, year],
    queryFn: () => api.get<SickLeaveResponse>(
      `/api/personen/${mainId}/sick-leave?year=${year}`,
    ),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 60_000,
  })
}

// ── Compliance / Certifications ───────────────────────────────────────────────

export type CertStatus = 'missing' | 'valid' | 'expires-soon' | 'expired'

export interface CertificationType {
  certTypeId: number
  code: string
  label: string
  description?: string | null
  defaultExpiryMonths: number | null
  requiresIssuer: boolean
  icon: string | null
  sortOrder: number
}

export interface PersonCertificationRow {
  certTypeId: number
  code: string
  label: string
  icon: string | null
  sortOrder: number
  defaultExpiryMonths: number | null
  requiresIssuer: boolean
  certificationId: number | null
  issuedAt: string | null
  expiresAt: string | null
  issuingAuthority: string | null
  notes: string | null
  status: CertStatus
}

export function useCertifications(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person-certifications', mainId],
    queryFn: () => api.get<{ certifications: PersonCertificationRow[] }>(
      `/api/personen/${mainId}/certifications`,
    ),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 60_000,
  })
}

export interface CertificationCreate {
  certTypeId: number
  issuedAt: string
  expiresAt?: string | null
  issuingAuthority?: string | null
  notes?: string | null
}

export function useCreateCertification(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CertificationCreate) =>
      api.post<{ certificationId: number; issuedAt: string; expiresAt: string | null }>(
        `/api/personen/${mainId}/certifications`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-person-certifications', mainId] }),
  })
}

export function useDeleteCertification(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ certificationId: number; archived: boolean }>(`/api/certifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-person-certifications', mainId] }),
  })
}

// ── Offboarding state machine ─────────────────────────────────────────────────

export type OffboardingState =
  | 'draft' | 'exit_confirmed' | 'in_progress' | 'archive_ready'
  | 'archive_done' | 'on_hold' | 'cancelled'

export type OffboardingReason =
  | 'kuendigung_an' | 'kuendigung_ag' | 'aufhebung' | 'befristung_ablauf' | 'tod'

export type DocumentsStatus = 'pending' | 'collected' | 'waived'
export type DatevStatus     = 'pending' | 'submitted' | 'done' | 'waived'
export type AccessStatus    = 'pending' | 'revoked' | 'waived'
export type GdprStatus      = 'pending' | 'exported' | 'delivered' | 'no_request'
export type CleanupStatus   = 'pending' | 'done'

export interface OffboardingDraft {
  draftId: number
  personId: number
  employmentId: number | null
  state: OffboardingState
  reasonCode: OffboardingReason | null
  noticeDate: string | null
  lastWorkday: string | null
  documentsStatus: DocumentsStatus
  datevStatus: DatevStatus
  accessStatus: AccessStatus
  gdprStatus: GdprStatus
  cleanupStatus: CleanupStatus
  gdprExportPath: string | null
  retentionLockUntil: string | null
  archiveDoneAt: string | null
  cancelledAt: string | null
  cancellationReason: string | null
  notes: string | null
  initiatedBy: string
  initiatedAt: string
}

export interface OffboardingTransition {
  transitionId: number
  fromState: OffboardingState | null
  toState: OffboardingState
  actor: string
  occurredAt: string
  reason: string | null
  payloadJson: string | null
}

export interface OffboardingResponse {
  draft: OffboardingDraft | null
  transitions: OffboardingTransition[]
  validNextStates: OffboardingState[]
}

export function useOffboarding(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person-offboarding', mainId],
    queryFn: () => api.get<OffboardingResponse>(`/api/personen/${mainId}/offboarding`),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 30_000,
  })
}

export interface OffboardingInitiate {
  reason_code?: OffboardingReason | null
  notice_date?: string | null
  last_workday?: string | null
  notes?: string | null
}

export function useInitiateOffboarding(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OffboardingInitiate) =>
      api.post<{ draftId: number; state: OffboardingState }>(
        `/api/personen/${mainId}/offboarding`, body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-person-offboarding', mainId] }),
  })
}

export type OffboardingPatch = Partial<{
  state: OffboardingState
  transition_reason: string | null
  cancellation_reason: string
  reason_code: OffboardingReason | null
  notice_date: string | null
  last_workday: string | null
  documents_status: DocumentsStatus
  datev_status: DatevStatus
  access_status: AccessStatus
  gdpr_status: GdprStatus
  cleanup_status: CleanupStatus
  gdpr_export_path: string | null
  retention_lock_until: string | null
  notes: string | null
}>

export function usePatchOffboarding(mainId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ draftId, body }: { draftId: number; body: OffboardingPatch }) =>
      api.patch<{ draftId: number; state: OffboardingState; transitioned: boolean }>(
        `/api/offboarding/${draftId}`, body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr-person-offboarding', mainId] }),
  })
}

// ── Bank & Steuer (sensitive — gated on hr-person-bank-steuer) ────────────────

export interface BankSteuer {
  iban: string | null
  bankverbindung: string | null
  steueridentifikationsnummer: string | null
  sozialversicherungsnummer: string | null
  krankenkasse: string | null
  religion: string | null
  familienstand: string | null
  kinderanzahl: number | null
  kinderfreibetrag: number | null
  elterneigenschaft: string | null
  steuerklasse: number | null
  kirchensteuer: string | null
  rentenversicherungsbefreiung: string | null
  geldvorteilTankkarte: string | null
  geldvorteilWunschgutschein: string | null
  geldvorteilSonstiges: string | null
  betrieblicheAltersversorgung: string | null
}

export function useBankSteuer(mainId: number | null | undefined) {
  return useQuery({
    queryKey: ['hr-person-bank-steuer', mainId],
    queryFn: () => api.get<{ bankSteuer: BankSteuer; readSource: 'dim' | 'fm' }>(
      `/api/personen/${mainId}/bank-steuer`,
    ),
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 60_000,
  })
}

// ── Audit log (audit.EventLog filtered by person) ─────────────────────────────

export interface AuditEvent {
  id: number
  occurredAt: string
  actorUser: string | null
  actorRole: string | null
  entityType: string
  entityId: string
  action: string
  diffJson: string | null
  source: string | null
}

export interface AuditPage {
  events: AuditEvent[]
  nextCursor: number | null
}

export function usePersonAudit(mainId: number | null | undefined, limit = 50) {
  // Let useInfiniteQuery infer TData → InfiniteData<AuditPage, …>. Passing
  // AuditPage as the TData generic (3rd slot) breaks `data.pages` access.
  return useInfiniteQuery({
    queryKey: ['hr-person-audit', mainId],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(limit) })
      if (pageParam != null) qs.set('cursorAfter', String(pageParam))
      return api.get<AuditPage>(`/api/personen/${mainId}/audit?${qs}`)
    },
    getNextPageParam: (last: AuditPage): number | null => last.nextCursor,
    enabled: typeof mainId === 'number' && mainId > 0,
    staleTime: 30_000,
  })
}
